import { CellSort, EmptyRowsFallback } from '@shared/ui';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { DataTable } from '@shared/ui/DataTable';
import {
  type Vacancy as VacancyType,
  useGetVacancyCopy,
} from '@entities/vacancy';
import type { VacancyCreateFormValues } from '../vacancyCreate/types';
import { type SortQuery } from '@shared/types';
import { VacancyStatusBadge } from '@widgets/vacancy/vacancyStatus';
import { ActionIcon, Tooltip } from '@mantine/core';
import { IconCopy } from '@tabler/icons-react';
import { formatDateTime, joinStringWithFallback } from '@shared/helpers';
import { getCityNames } from '@entities/region';

type VacancyListItem = Pick<
  VacancyType,
  'id' | 'name' | 'createdAt' | 'status'
> & {
  city?: Pick<NonNullable<VacancyType['city']>, 'id' | 'name'>;
  cities?: Array<
    Pick<NonNullable<VacancyType['cities']>[number], 'id' | 'name'>
  >;
  responsibleUser: {
    profile: Pick<
      VacancyType['responsibleUser']['profile'],
      'firstName' | 'lastName'
    >;
  };
};

type VacancyListProps = {
  vacancies: VacancyListItem[];
  citySortKey?: 'city.name' | 'cities.name';
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  recordsPerPageOptions?: number[];
  onRecordsPerPageChange?: (pageSize: number) => void;
  setPageSize: (pageSize: number) => void;
  isFilledFilters: boolean;
  onSortChange: (field: string, direction?: 'asc' | 'desc') => void;
  currentSort: SortQuery;
  totalRecords?: number;
  isLoading: boolean;
};

export const VacancyList = ({
  vacancies,
  citySortKey = 'cities.name',
  page,
  setPage,
  pageSize,
  recordsPerPageOptions,
  onRecordsPerPageChange,
  setPageSize,
  isFilledFilters,
  onSortChange,
  currentSort,
  totalRecords,
  isLoading,
}: VacancyListProps) => {
  const navigate = useNavigate();
  const { mutateAsync, isPending } = useGetVacancyCopy();
  const [copyingId, setCopyingId] = useState<number | null>(null);

  const handleCopyVacancy = (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    setCopyingId(id);
    mutateAsync(id, {
      onSuccess: data => {
        setCopyingId(null);
        const initialValues: VacancyCreateFormValues = {
          name: data.name || '',
          cityIds: data.cities?.map(city => city.id.toString()) ?? [],
          companyId: data.company.id.toString(),
          responsibleUserId: data.responsibleUser.id.toString(),
          interviewFinalMessage: data.interviewFinalMessage || '',
          requiredCandidatesCount: data.requiredCandidatesCount.toString(),
          ai_sphere_fill: data.ai_sphere_fill ?? false,
          activitySphereIds: data.activitySpheres.map(s => s.id.toString()),
          scheduleWorkIds: data.scheduleWorks.map(s => s.id.toString()),
          employmentTypeIds: data.employmentTypes.map(s => s.id.toString()),
          skills: data.skills.map(s => s.name.toString()),
          currencyId: data.currency.id.toString(),
          salaryFrom: data.salaryFrom,
          salaryTo: data.salaryTo,
          paymentPeriodId: data.paymentPeriod.id.toString(),
          tariffId: data.tariffs.id.toString(),
          mandatoryRequirements: data.mandatoryRequirements || '',
          additionalRequirements: data.additionalRequirements || '',
          responsibilities: data.responsibilities || '',
          activeFrom: data.vacancyActivities?.[0]?.activeFrom
            ? new Date(data.vacancyActivities[0].activeFrom)
            : null,
          activeTo: data.vacancyActivities?.[0]?.activeTo
            ? new Date(data.vacancyActivities[0].activeTo)
            : null,
        };

        navigate('/vacancy-list/create', { state: initialValues });
      },
    });
  };

  const columns = useMemo(
    () => [
      {
        accessor: 'id',
        title: (
          <CellSort
            label='ID'
            onSort={dir => onSortChange('id', dir)}
            currentDirection={currentSort.id}
          />
        ),
      },
      {
        accessor: 'name',
        width: 300,
        title: (
          <CellSort
            label='Вакансия'
            onSort={dir => onSortChange('name', dir)}
            currentDirection={currentSort.name}
          />
        ),
      },
      {
        accessor: 'createdAt',
        title: (
          <CellSort
            label='Дата создания'
            onSort={dir => onSortChange('createdAt', dir)}
            currentDirection={currentSort.createdAt}
          />
        ),
        render: ({ createdAt }: VacancyListItem) => formatDateTime(createdAt),
      },
      {
        accessor: 'responsibleUser.profile.fullName',
        title: (
          <CellSort
            label='Сотрудник'
            onSort={dir =>
              onSortChange('responsibleUser.profile.fullName', dir)
            }
            currentDirection={currentSort['responsibleUser.profile.fullName']}
          />
        ),
        render: ({ responsibleUser }) => {
          const profile = responsibleUser.profile;
          return joinStringWithFallback([profile.firstName, profile.lastName], {
            fallback: '-',
          });
        },
      },
      {
        accessor: citySortKey === 'city.name' ? 'city' : 'cities',
        title: (
          <CellSort
            label='Город'
            onSort={dir => onSortChange(citySortKey, dir)}
            currentDirection={currentSort[citySortKey]}
          />
        ),
        render: (vacancy: VacancyListItem) => getCityNames(vacancy),
      },
      {
        accessor: 'status',
        title: (
          <CellSort
            label='Статус'
            onSort={dir => onSortChange('status', dir)}
            currentDirection={currentSort.status}
          />
        ),
        render: (record: VacancyListItem) => (
          <VacancyStatusBadge status={record.status} />
        ),
      },
      {
        accessor: 'actions',
        title: 'Действия',
        render: (record: VacancyListItem) => (
          <Tooltip
            label='Скопировать'
            withArrow
            bg='secondary.2'
            offset={10}
            position='top-end'
            c='primary.4'
            fz={12}
            arrowOffset={15}
            arrowSize={8}
            arrowRadius={4}
          >
            <ActionIcon
              aria-label={`Скопировать вакансию ${record.name}`}
              my={-5}
              variant='subtle'
              size={32}
              loading={isPending && copyingId === record.id}
              disabled={isPending}
              onClick={e => handleCopyVacancy(e, record.id)}
            >
              <IconCopy size={12} color='var(--mantine-color-primary-7)' />
            </ActionIcon>
          </Tooltip>
        ),
      },
    ],
    [citySortKey, currentSort, onSortChange, isPending, copyingId]
  );

  const handleRowClick = ({ record }: { record: VacancyListItem }) => {
    navigate(`/vacancy-list/${record.id}`);
  };

  const emptyState = useMemo(() => {
    if (isFilledFilters) {
      return {
        desc: 'К сожалению, совпадений не найдено',
        subDesc:
          'Попробуйте изменить параметры фильтров. Чтобы вернуться ко всему списку, сбросьте фильтр.',
      };
    }

    return {
      desc: 'Список вакансий пуст',
      subDesc:
        'Чтобы создать вакансию в компании, нажмите кнопку «Создать вакансию»',
    };
  }, [isFilledFilters]);

  return (
    <DataTable<VacancyListItem>
      fetching={isLoading}
      columns={columns}
      records={vacancies}
      onRowClick={handleRowClick}
      page={page}
      setPage={setPage}
      recordsPerPageOptions={recordsPerPageOptions}
      onRecordsPerPageChange={onRecordsPerPageChange}
      pageSize={pageSize}
      setPageSize={setPageSize}
      recordsPerPage={pageSize}
      onPageChange={setPage}
      totalRecords={totalRecords}
      emptyState={
        <EmptyRowsFallback
          desc={emptyState.desc}
          subDesc={emptyState.subDesc}
        />
      }
      highlightOnHover
    />
  );
};
